/**
 * Latency Classifier Utility
 *
 * Pure functions for classifying latency values into color-coded tiers
 * and formatting impact timestamps.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 5.1, 5.2
 */

import type { LatencyTierInfo } from '../models/shotLatencyModels';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Classifies a latency value (in milliseconds) into a tier with an associated color.
 *
 * | Tier       | Range (ms)  | Color                  |
 * |------------|-------------|------------------------|
 * | Excellent  | < 2000      | #22c55e (green)        |
 * | Good       | 2000–2999   | #84cc16 (yellow-green) |
 * | OK         | 3000–3999   | #eab308 (yellow/amber) |
 * | Poor       | 4000–5999   | #f97316 (orange)       |
 * | Bad        | ≥ 6000      | #ef4444 (red)          |
 */
export function classifyLatency(latencyMs: number): LatencyTierInfo {
  if (latencyMs < 2000) {
    return { tier: 'Excellent', color: '#22c55e' };
  }
  if (latencyMs < 3000) {
    return { tier: 'Good', color: '#84cc16' };
  }
  if (latencyMs < 4000) {
    return { tier: 'OK', color: '#eab308' };
  }
  if (latencyMs < 6000) {
    return { tier: 'Poor', color: '#f97316' };
  }
  return { tier: 'Bad', color: '#ef4444' };
}

/**
 * Formats an ISO 8601 impact time string into a human-readable format.
 *
 * - Within 24 hours: relative string (e.g., "2 minutes ago")
 * - Older than 24 hours: absolute date/time string
 * - Invalid input: returns the raw string as fallback
 */
export function formatImpactTime(impactTimeIso: string): string {
  const date = new Date(impactTimeIso);

  if (isNaN(date.getTime())) {
    return impactTimeIso;
  }

  const now = Date.now();
  const diffMs = now - date.getTime();

  if (diffMs < 0 || diffMs >= TWENTY_FOUR_HOURS_MS) {
    return date.toLocaleString();
  }

  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) {
    return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
}
